<template>
  <BaseModalEditCreate
    v-if="isOpen"
    maxWidth="narrow"
    :disable="mode === 'view'"
    :isModified="shouldWarnBeforeClose"
    :title="modifiedData.createdDate ? $t('common.actions.create') : modifiedData?.description"
    :mode="mode == 'edit' && props.item?.createdDate ? 'edit' : 'add'"
    @save="onSave"
    @dismissChanges="onDismissChanges">
    <template #form>
      <div class="space-y-5">
        <div>
          <label for="description" class="text-sm font-medium">{{
            $t('questionnaire.description')
          }}</label>
          <BaseInput
            v-model="modifiedData.description"
            color="base"
            :disabled="mode === 'view'"
            show-error-as-tooltip
            :error="getInputErrors('description')" />
        </div>
        <div>
          <label for="sort" class="text-sm font-medium">{{ $t('question.sort') }}</label>
          <BaseInput
            v-model="modifiedData.sort"
            color="base"
            :disabled="mode === 'view'"
            show-error-as-tooltip
            :error="getInputErrors('sort')" />
        </div>
        <div @dragstart.prevent @drop.prevent>
          <label for="image" class="text-sm font-medium">{{ $t('question.image') }}</label>
          <ImageUpload
            v-if="mode == 'edit'"
            :image="image"
            class="my-2"
            @image-changed="onPhotoChange" />
          <span v-else><img v-if="image" :src="image" /></span>
        </div>
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { helpers, required, minValue, integer } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import BaseInput from '../common/BaseInput.vue';
import BaseModalEditCreate from '../common/BaseModalEditCreate.vue';
import ImageUpload from '../common/ImageUpload.vue';

import type { QstListRow } from '@/types/Questionnaires';
import { useI18n } from 'vue-i18n';
import { convertBlobToBase64 } from '@/utils/image';
import { deepCopy } from '@/utils/data';

const { t: $t } = useI18n({ useScope: 'global' });

const props = defineProps<{
  item: QstListRow;
  isOpen: boolean;
  mode: 'view' | 'edit';
}>();

const emit = defineEmits(['close', 'save']);

const modifiedData = ref<QstListRow>(deepCopy<QstListRow>(props.item));

const shouldWarnBeforeClose = ref<boolean>(false);

const validationModel = ref({
  description: modifiedData.value.description || '',
  sort: modifiedData.value.sort || 0,
});
const rules = computed(() => ({
  description: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  sort: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
    minValue: helpers.withMessage(`${$t('messages.errors.invalidValue')}`, minValue(0)),
    integer: helpers.withMessage(`${$t('messages.errors.invalidValue')}`, integer),
  },
}));
const $v = useVuelidate(rules, validationModel, { $lazy: true, $scope: false });

function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;

  shouldWarnBeforeClose.value = false;
  emit('save', modifiedData.value);
  $v.value.$reset();
}

function onDismissChanges() {
  modifiedData.value = deepCopy<QstListRow>(props.item);
  $v.value.$reset();
  emit('close');
}

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.description = newData.description || '';
    validationModel.value.sort = newData.sort || 0;
    shouldWarnBeforeClose.value = JSON.stringify(modifiedData.value) !== JSON.stringify(props.item);
  },
  { deep: true }
);

function getInputErrors(type: 'description' | 'sort') {
  if ($v.value[type].required && $v.value[type].required.$invalid)
    return $v.value[type].required.$message;
  else if ($v.value[type].minValue && $v.value[type].minValue.$invalid)
    return $v.value[type].minValue.$message;
  else if ($v.value[type].integer && $v.value[type].integer.$invalid)
    return $v.value[type].integer.$message;
  else return '';
}

watch(
  () => props.item,
  (newData) => {
    modifiedData.value = deepCopy<QstListRow>(newData);
  }
);

onMounted(async () => {
  modifiedData.value = deepCopy<QstListRow>(props.item);
});

const image = computed(() => {
  if (!modifiedData.value) return;
  if (modifiedData.value.image && !modifiedData.value.image.includes('data:image'))
    return `modifiedData:image/jpeg;base64,${modifiedData.value?.image}`;
  return modifiedData.value.image;
});

function onPhotoChange(image: string) {
  if (!modifiedData.value) return;
  if (!image) modifiedData.value.image = undefined;
  if (image && image.startsWith('blob')) {
    convertBlobToBase64(image)
      .then((base64Image) => {
        if (modifiedData.value)
          modifiedData.value.image = base64Image.replace(/^data:image\/[a-zA-Z]+;base64,/, '');
      })
      .catch((error) => {
        console.error('Error converting blob to Base64:', error);
      });
  } else if (image && image.startsWith('data:image/,') && image.endsWith(';base64,')) {
    modifiedData.value.image = image.replace(/^data:image\/[a-zA-Z]+;base64,/, '');
  }
}
</script>
